import { useEffect } from 'react';
import {
  InstantSearch as AlgoliaInstantSearch,
  InstantSearchSSRProvider,
  useInstantSearch,
} from 'react-instantsearch';
import algoliasearch from 'algoliasearch/lite';
import { debounce } from 'lodash-es';

import { gtmPush } from '@/utils/gtm/helpers';
import { convertToURL } from '../utils';

import type { FunctionComponent } from 'react';
import type { TypeInstantSearchProps } from './types';

const algoliaClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_APP_ID ?? '',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY ?? '',
);

function gtmMiddleware(results: any) {
  const sendEventDebounced = debounce((uiState: any) => {
    const indexName = Object.keys(uiState)[0];
    const searchQuery = uiState[indexName].query;

    if (searchQuery) {
      if (results.nbHits === 0) {
        gtmPush({
          event: 'search_unavailable',
          search_term: searchQuery,
        });
      } else {
        gtmPush({
          event: 'search',
          search_term: searchQuery,
          search_results_count: results.nbHits,
          search_results: results.hits.map((hit: any) => ({
            item_id: convertToURL(hit.objectID.replaceAll(' > ', '/')),
            item_name: hit.name,
          })),
        });
      }
    }
  }, 3000);

  return {
    onStateChange({ uiState }: any) {
      sendEventDebounced(uiState);
    },

    subscribe() {},

    unsubscribe() {},
  };
}

function Middleware() {
  const { addMiddlewares, results } = useInstantSearch();

  useEffect(() => addMiddlewares(() => gtmMiddleware(results)));

  return null;
}

/**
 * InstantSearch
 */
export const InstantSearch: FunctionComponent<TypeInstantSearchProps> = ({
  children,
  ssrPlaceholder = false,
  ...props
}: TypeInstantSearchProps) => {
  const search = (
    <AlgoliaInstantSearch searchClient={algoliaClient} {...props}>
      {children}
      <Middleware />
    </AlgoliaInstantSearch>
  );

  // `ssrPlaceholder`: envuelve en un `InstantSearchSSRProvider` con
  // resultados vacíos. Sin provider, el `<InstantSearch>` de
  // react-instantsearch v7 no renderiza NADA en servidor: el buscador del
  // hero aparecía sólo al hidratar y el hero crecía ~400px de golpe (salto
  // de layout en cada carga). Con el provider, el estado "cerrado" del
  // formulario (que no necesita datos) llega ya en el HTML de SSR.
  //
  // Es OPT-IN a propósito: ListOfFlats monta su propio provider por fuera
  // con el serverState real, y anidar este vacío por dentro lo pisaría.
  return ssrPlaceholder ? (
    <InstantSearchSSRProvider initialResults={{}}>
      {search}
    </InstantSearchSSRProvider>
  ) : (
    search
  );
};

InstantSearch.displayName = 'InstantSearch';
